import { FaroCoreApiUtils } from "@stellar/core-api-client";

/**
 * The workspace roles that only a member holding a Sphere XG seat may take on, since they let the
 * member make changes: admins, group managers and project managers.
 */
const XG_SEAT_WORKSPACE_ROLES = new Set(["COMPANY_EXECUTIVE", "COMPANY_MANAGER", "PROJECT_MANAGER"]);

/** Whether a member needs a Sphere XG seat to take on a workspace role */
export function doesWorkspaceRoleRequireXgSeat(role) {
  return XG_SEAT_WORKSPACE_ROLES.has(role);
}

/**
 * Returns why a member cannot be given a Sphere XG seat, or undefined when they can be given one.
 *
 * Any member can hold a seat, including one that did not accept their workspace invite yet. Teams
 * are not users at all and can therefore never hold one.
 */
export function getXgSeatBlockReason(member) {
  if (FaroCoreApiUtils.Member.isTeam(member)) {
    return "Teams cannot hold a Sphere XG seat";
  }
  return undefined;
}

/**
 * Returns the keys to look members up in the active members, which are their member ID and email.
 *
 * The email is included because a pending member can be listed with an ID in one response and
 * without it in the other, while the email is the same in both.
 */
export function getActiveMemberKeys(members) {
  return new Set(members.flatMap(member => getMemberKeys(member)));
}

/**
 * Whether a member is one of the active members of the workspace, who hold a Sphere XG seat when
 * the plan limits the active users.
 *
 * @param member The member to check
 * @param activeMemberKeys The keys of the active members of the workspace, see getActiveMemberKeys
 */
export function isActiveMember(member, activeMemberKeys) {
  return getMemberKeys(member).some(key => activeMemberKeys.has(key));
}

/** Returns the keys that identify a member in the active members */
function getMemberKeys(member) {
  if (FaroCoreApiUtils.Member.isTeam(member)) {
    return [];
  }
  const keys = [FaroCoreApiUtils.Member.getMemberId(member)];
  if ("email" in member && member.email) {
    keys.push(member.email.toLowerCase());
  }
  return keys;
}